import { differenceInHours, differenceInMinutes } from "date-fns";

export type QueueUrgency = "green" | "yellow" | "red" | "unknown";

export interface QueueTimeInfo {
  readonly hours: number;
  readonly urgency: QueueUrgency;
  readonly labelKey: string;
  readonly labelValues: { readonly hours?: number; readonly minutes?: number };
}

/**
 * Compute how long a venue has been waiting in the queue and its SLA
 * urgency level.
 *
 * - green:   < 24 h — within normal processing time
 * - yellow:  24–48 h — needs attention, approaching SLA deadline
 * - red:     > 48 h — at risk of breaching the 72 h SLA
 * - unknown: unparseable createdAt (data boundary guard)
 */
export function getQueueTimeInfo(createdAt: string, now: Date = new Date()): QueueTimeInfo {
  const submitted = new Date(createdAt);

  // Guard against unparseable dates — NaN propagates through Math.max
  if (Number.isNaN(submitted.getTime())) {
    return {
      hours: 0,
      urgency: "unknown",
      labelKey: "venues.queue.timeUnknown",
      labelValues: {},
    };
  }

  // Clamp to 0 to handle clock skew (future createdAt)
  const hours = Math.max(0, differenceInHours(now, submitted));
  const minutes = Math.max(0, differenceInMinutes(now, submitted));

  const urgency: QueueUrgency = hours >= 48 ? "red" : hours >= 24 ? "yellow" : "green";

  if (hours < 1) {
    return {
      hours: 0,
      urgency,
      labelKey: "venues.queue.timeMinutes",
      labelValues: { minutes },
    };
  }

  return {
    hours,
    urgency,
    labelKey: "venues.queue.timeHours",
    labelValues: { hours },
  };
}
